import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import InputText from "../../components/form-control/input-text";
import { InputTextarea } from "primereact/inputtextarea";
import apiFunctions from "../../services/api-service";
import { Select, Spin } from "antd";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import useToast from "../../components/common/useToast";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import AdvertisementCard from "../../components/common/AdvertisementCard";
import { InputText as PrimeInput } from "primereact/inputtext";
import { useJsApiLoader } from "@react-google-maps/api";

const { Option } = Select;

const GOOGLE_LIBRARIES: ("places")[] = ["places"];

interface FormValues {
    company_name: string;
    short_description: string;
    long_description: string;
    whatsapp_number: string;
    mobile_number: string;
    email: string;
    website_link: string;
    city: string;
    page_name: string[];
    ad_type: string;
    amount: number;

    package_id: string;
    start_date: Date | null;
    end_date: Date | null;
}

const options = [
    { label: "All", value: "all" },
    { label: "Home Post", value: "homepost" },
    { label: "Home Story", value: "homestory" },
    { label: "MaitriMonial", value: "maitrimonial" },
    { label: "Directory", value: "users" },
    { label: "Marketplace", value: "business" },
    { label: "Bazar Bhav", value: "bazarbhav" },
    { label: "Jobs", value: "job" },
    { label: "Blood Group", value: "bloodgroup" },
    { label: "Notification", value: "notification" },
];

const validationSchema = Yup.object().shape({
    company_name: Yup.string().required("Company name required"),
    short_description: Yup.string().max(30, "Maximum 30 characters allowed"),
    long_description: Yup.string().max(500, "Maximum 500 characters allowed"),
    mobile_number: Yup.string().required("Mobile required"),
    ad_type: Yup.string().required("Ad type required"),
});

// Business/Product/Job store plain 10-digit mobile numbers (no country code),
// but this form's PhoneInput fields expect "+91XXXXXXXXXX". Prefix it if missing.
const withCountryCode = (num?: string) => {
    if (!num) return "";
    const digits = num.replace(/\D/g, "");
    if (!digits) return "";
    if (digits.startsWith("91") && digits.length > 10) return `+${digits}`;
    return `+91${digits}`;
};

const formatDate = (date: Date | null) => {
    if (!date) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
};

const AdvertisementAdd: React.FC = () => {

    const { userid, adid } = useParams();
    const location = useLocation();
    const { showToast } = useToast();

    const [selectedUserId, setSelectedUserId] = useState<string>(userid || "");
    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [userSearchValue, setUserSearchValue] = useState('');
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [firstUser, setFirstUser] = useState(0);
    const [totalUsersCount, setTotalUsersCount] = useState(0);
    const [rows] = useState(10);

    const [advertisementId, setAdvertisementId] = useState<string | null>(null);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY || "",
        libraries: GOOGLE_LIBRARIES,
    });
    const cityInputRef = React.useRef<HTMLInputElement | null>(null);

    const [files, setFiles] = useState<File[]>([]);
    const [previews, setPreviews] = useState<any[]>([]);

    const [loading, setLoading] = useState(false);

    // Google Places autocomplete on Targeted City field
    useEffect(() => {
        if (!isLoaded || !cityInputRef.current) return;
        const timer = setTimeout(() => {
            if (!cityInputRef.current) return;
            const autocomplete = new (window as any).google.maps.places.Autocomplete(
                cityInputRef.current,
                { types: ["(cities)"] }
            );
            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (!place.place_id) return;
                const components = place.address_components || [];
                const getComp = (type: string) =>
                    components.find((c: any) => c.types.includes(type))?.long_name || "";
                const cityName =
                    getComp("locality") ||
                    getComp("administrative_area_level_2") ||
                    place.name || "";
                formik.setFieldValue("city", cityName);
            });
        }, 100);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded]);

    /* ---------- User search ---------- */

    const fetchingUsersRef = React.useRef(false);
    const ownerOptionRef = React.useRef<any>(null);

    const dedupeUsers = (list: any[]) =>
        list.filter((u, i, arr) => arr.findIndex(x => (x.id || x._id) === (u.id || u._id)) === i);

    const fetchUsers = async (reset = false) => {
        if (fetchingUsersRef.current) return;
        fetchingUsersRef.current = true;
        setFetchingUsers(true);
        const skip = reset ? 0 : firstUser;
        try {
            let url = `/user/dropdownlist/${skip}/${rows}`;
            if (userSearchValue && userSearchValue.length > 1) url += `/${userSearchValue}`;
            const response = await apiFunctions.get(url);
            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;
            if (reset) {
                setUserOptions(dedupeUsers([
                    ...(ownerOptionRef.current ? [ownerOptionRef.current] : []),
                    ...newItems
                ]));
                setFirstUser(rows);
            } else {
                setUserOptions(prev => dedupeUsers([...prev, ...newItems]));
                setFirstUser(prev => prev + rows);
            }
            setTotalUsersCount(total);
        } finally {
            fetchingUsersRef.current = false;
            setFetchingUsers(false);
        }
    };

    useEffect(() => {
        setFirstUser(0);
        fetchUsers(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSearchValue]);

    const handleUserScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;
        if (scrollTop + offsetHeight >= scrollHeight - 10 && userOptions.length < totalUsersCount && !fetchingUsers) {
            fetchUsers();
        }
    };

    /* Handle File */
    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {

        const selectedFiles = Array.from(e.target.files || []);

        const validFiles = selectedFiles.filter(file => {

            const isImage = file.type.startsWith("image/");
            const isVideo = file.type.startsWith("video/");

            if (!isImage && !isVideo) {
                showToast({ severity: "warn", summary: "Invalid File", detail: "Only JPG, PNG, MP4 allowed", life: 3000 });
                return false;
            }

            // if (file.size > 20 * 1024 * 1024) {
            //   alert("File must be less than 20MB");
            //   return false;
            // }

            return true;

        });

        setFiles(validFiles);

        const previewItems = validFiles.map(file => ({
            url: URL.createObjectURL(file),
            file,
            type: file.type.startsWith("video") ? "video" : "image"
        }));

        setPreviews(previewItems);

    };

    /* Submit */
    const formik = useFormik<FormValues>({
        initialValues: {
            company_name: "",
            short_description: "",
            long_description: "",
            whatsapp_number: "",
            mobile_number: "",
            email: "",
            website_link: "",
            city: "",
            page_name: [],
            ad_type: "image",
            amount: 0,
            package_id: "",
            start_date: null,
            end_date: null,
        },
        validationSchema,
        onSubmit: async (values) => {

            try {

                setLoading(true);

                const formData = new FormData();

                formData.append("userid", selectedUserId);
                formData.append("company_name", values.company_name);
                formData.append("long_description", values.long_description);
                formData.append("short_description", values.short_description);
                formData.append("target_city", values.city || "");
                formData.append("email", values.email);
                formData.append("website_link", values.website_link);
                formData.append("whatsapp_number", values.whatsapp_number);
                formData.append("mobile_number", values.mobile_number);
                formData.append(
                    "page_name",
                    JSON.stringify(values.page_name || [])
                );
                formData.append("ad_type", values.ad_type);
                formData.append("start_date", formatDate(values.start_date));
                formData.append("end_date", formatDate(values.end_date));
                formData.append("is_active", "true");

                files.forEach(file => formData.append("media", file));

                // UPDATE
                if (advertisementId) {

                    await apiFunctions.put(
                        `/advertisement/admin/update/${advertisementId}`,
                        formData,
                        undefined,
                        true
                    );

                    showToast({ severity: "success", summary: "Updated", detail: "Advertisement updated successfully!", life: 3000 });

                }

                // CREATE
                else {

                    await apiFunctions.post(
                        "/advertisement/admin/create",
                        formData,
                        null,
                        true
                    );

                    showToast({ severity: "success", summary: "Created", detail: "Advertisement created successfully!", life: 3000 });

                    formik.resetForm();

                    setFiles([]);

                    setPreviews([]);

                }

            } catch (err) {

                console.log(err);

                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: advertisementId ? "Error updating advertisement" : "Error creating advertisement",
                    life: 4000
                });

            } finally {

                setLoading(false);

            }

        }
    });

    const handleSectionChange = (e: any) => {

        let value = e.value;

        const allOption = "all";

        const normalOptions = options
            .filter(o => o.value !== allOption)
            .map(o => o.value);

        // If ALL selected
        if (value.includes(allOption)) {

            formik.setFieldValue("page_name", [allOption, ...normalOptions]);
            return;

        }

        // If user selected all manually
        const allSelected = normalOptions.every(v => value.includes(v));

        if (allSelected) {

            formik.setFieldValue("page_name", [allOption, ...normalOptions]);

        } else {

            formik.setFieldValue(
                "page_name",
                value.filter((v: string) => v !== allOption)
            );

        }

    };

    useEffect(() => {
        return () => {
            previews.forEach(p => URL.revokeObjectURL(p.url));
        };
    }, [previews]);

    useEffect(() => {
        if (!loading) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = "";
        };

        const handleKeyDown = (e: KeyboardEvent) => {
            if (
                e.key === "F5" ||
                (e.ctrlKey && e.key.toLowerCase() === "r")
            ) {
                e.preventDefault();
            }
        };

        const handlePopState = () => {
            window.history.pushState(null, "", window.location.href);
        };

        // Push state to block back button
        window.history.pushState(null, "", window.location.href);

        window.addEventListener("beforeunload", handleBeforeUnload);
        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("popstate", handlePopState);
        };
    }, [loading]);

    useEffect(() => {
        if (!adid) return;

        setAdvertisementId(adid);

        const fetchAdForEdit = async () => {
            try {
                const response = await apiFunctions.get(`/advertisement/${adid}`);
                if (!response?.data?.success) return;

                const ad = response.data.data;

                formik.setValues({
                    company_name: ad.company_name || "",
                    short_description: ad.short_description || "",
                    long_description: ad.long_description || "",
                    whatsapp_number: ad.whatsapp_number || "",
                    mobile_number: ad.mobile_number || "",
                    email: ad.email || "",
                    website_link: ad.website_link || "",
                    city: ad.target_city || "",
                    page_name: ad.page_name || [],
                    ad_type: ad.ad_type || "image",
                    amount: 0,
                    package_id: "",
                    start_date: ad.start_date ? new Date(ad.start_date) : null,
                    end_date: ad.end_date ? new Date(ad.end_date) : null,
                });

                if (ad.media && ad.media.length > 0) {
                    setPreviews(ad.media.map((m: any) => ({
                        url: m.media_url,
                        type: m.media_type === "video" ? "video" : "image",
                        existing: true,
                    })));
                }

                // Auto-select ad owner in the user dropdown
                if (ad.user_id) {
                    setSelectedUserId(ad.user_id);
                    ownerOptionRef.current = { id: ad.user_id, name: ad.full_name || ad.user_id, mobileno: ad.mobile_no || "" };
                    setUserOptions(prev =>
                        prev.some(u => (u.id || u._id) === ad.user_id)
                            ? prev
                            : [ownerOptionRef.current, ...prev]
                    );
                }
            } catch (err) {
                console.error("Edit fetch error:", err);
            }
        };

        fetchAdForEdit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adid]);

    // Prefill from Business/Product/Job "Create Advertisement" shortcut
    useEffect(() => {
        if (adid) return; // edit mode already loads its own values

        const prefill = (location.state as any)?.prefill;
        if (!prefill) return;

        formik.setValues((prev) => ({
            ...prev,
            company_name: prefill.company_name || prev.company_name,
            short_description: prefill.short_description || prev.short_description,
            long_description: prefill.long_description || prev.long_description,
            mobile_number: prefill.mobile_number ? withCountryCode(prefill.mobile_number) : prev.mobile_number,
            whatsapp_number: prefill.whatsapp_number ? withCountryCode(prefill.whatsapp_number) : prev.whatsapp_number,
            email: prefill.email || prev.email,
            website_link: prefill.website_link || prev.website_link,
            city: prefill.city || prev.city,
        }));

        if (prefill.userid) {
            setSelectedUserId(prefill.userid);
            ownerOptionRef.current = {
                id: prefill.userid,
                name: prefill.ownerName || prefill.userid,
                mobileno: prefill.mobile_number || "",
            };
            setUserOptions(prev =>
                prev.some(u => (u.id || u._id) === prefill.userid)
                    ? prev
                    : [ownerOptionRef.current, ...prev]
            );
        }

        // Pull the business logo / product image / job company logo in as the ad's media
        if (prefill.imageUrl) {
            (async () => {
                try {
                    const blob = await fetch(prefill.imageUrl).then(r => r.blob());
                    const ext = blob.type.split("/")[1] || "jpg";
                    const file = new File([blob], `prefill-image.${ext}`, { type: blob.type });

                    setFiles([file]);
                    setPreviews([{
                        url: URL.createObjectURL(file),
                        file,
                        type: "image",
                    }]);
                } catch (err) {
                    console.error("Failed to prefill ad image:", err);
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adid, location.state]);

    return (
        <div className="min-h-screen bg-gray-100" >

            {/* Main */}
            <div className="max-w-5xl mx-auto sm:p-6 p-4 grid md:grid-cols-2 gap-6 items-start">

                {/* LEFT FORM */}
                <form
                    onSubmit={formik.handleSubmit}
                    className="bg-white p-6 rounded-xl shadow"
                >

                    <h2 className="text-xl font-semibold mb-4">
                        {adid ? "Edit Advertisement" : "Add Advertisement"}
                    </h2>

                    {/* User Select */}
                    <div className="field mb-4">
                        <label className="form-label">Select User (Optional)</label>
                        <Select
                            allowClear
                            showSearch
                            style={{ width: "100%", height: "42px" }}
                            placeholder="Search by name or mobile..."
                            filterOption={false}
                            onSearch={(val) => setUserSearchValue(val)}
                            onPopupScroll={handleUserScroll}
                            notFoundContent={fetchingUsers ? <Spin size="small" /> : "No users found"}
                            value={selectedUserId || undefined}
                            onChange={(value) => setSelectedUserId(value ?? "")}
                            onClear={() => setSelectedUserId("")}
                        >
                            {userOptions.map(user => (
                                <Option key={user.id || user._id} value={user.id || user._id}>
                                    {user.name || user.full_name}{user.mobileno ? ` - ${user.mobileno}` : ""}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <InputText
                        id="company_name"
                        label="Company Name"
                        modelValue={formik.values.company_name}
                        onUpdateModelValue={(val) =>
                            formik.setFieldValue("company_name", val)
                        }
                        validation={
                            formik.errors.company_name &&
                            formik.touched.company_name && {
                                $error: true,
                                required: {
                                    $invalid: true,
                                    $message: formik.errors.company_name
                                }
                            }
                        }
                    />

                    <div className="field mt-2">
                        <label className="form-label">Short Description</label>

                        <InputTextarea
                            value={formik.values.short_description}
                            onChange={(e) =>
                                formik.setFieldValue("short_description", e.target.value)
                            }
                            rows={2}
                            className={`w-full resize-y overflow-y-auto ${formik.touched.short_description &&
                                formik.errors.short_description
                                ? "p-invalid"
                                : ""
                                }`}
                            maxLength={30}
                        />

                        <div className="flex justify-between text-xs mt-1">
                            <span className="p-error">
                                {formik.touched.short_description &&
                                    formik.errors.short_description}
                            </span>

                            <span className="text-gray-500">
                                {formik.values.short_description.length}/30
                            </span>
                        </div>
                    </div>

                    <div className="field mt-2">
                        <label className="form-label">Long Description</label>

                        <InputTextarea
                            value={formik.values.long_description}
                            onChange={(e) =>
                                formik.setFieldValue("long_description", e.target.value)
                            }
                            rows={4}
                            className="w-full resize-y overflow-y-auto"
                            maxLength={500}
                        />

                        <div className="text-right text-xs text-gray-500 mt-1">
                            {formik.values.long_description.length}/500
                        </div>
                    </div>

                    {/* <InputText
            id="city"
            label="City"
            sectionClass="field mt-2"
            modelValue={formik.values.city}
            onUpdateModelValue={(val) =>
              formik.setFieldValue("city", val)
            }
          /> */}

                    <div className="field mt-2">
                        <label htmlFor={`cityname`} className={`form-label`}>
                            Targeted City
                        </label>
                        <PrimeInput
                            id="cityname"
                            ref={cityInputRef as any}
                            className={`w-full mt-1 ${formik.touched.city && formik.errors.city ? "p-invalid" : ""}`}
                            placeholder="Search city on Google Maps..."
                            value={formik.values.city || ""}
                            onChange={(e) => formik.setFieldValue("city", e.target.value)}
                        />
                        {formik.touched.city && typeof formik.errors.city === "string" && (
                            <span id="city-help" className="p-error text-left mt-2">
                                {formik.errors.city}
                            </span>
                        )}
                    </div>

                    <div className="field mt-2">
                        <label className="form-label">Mobile</label>
                        <PhoneInput
                            country="in"
                            value={formik.values.mobile_number}
                            onChange={(val) => formik.setFieldValue("mobile_number", `+${val}`)}
                            inputStyle={{ width: "100%", height: "42px" }}
                            containerStyle={{ width: "100%" }}
                        />
                        {formik.touched.mobile_number && formik.errors.mobile_number && (
                            <small className="p-error">{formik.errors.mobile_number}</small>
                        )}
                    </div>

                    <div className="field mt-2">
                        <label className="form-label">Whatsapp</label>
                        <PhoneInput
                            country="in"
                            value={formik.values.whatsapp_number}
                            onChange={(val) => formik.setFieldValue("whatsapp_number", `+${val}`)}
                            inputStyle={{ width: "100%", height: "42px" }}
                            containerStyle={{ width: "100%" }}
                        />
                    </div>

                    <InputText
                        id="email"
                        label="Email"
                        sectionClass="field mt-2"
                        modelValue={formik.values.email}
                        onUpdateModelValue={(val) =>
                            formik.setFieldValue("email", val)
                        }
                    />

                    <InputText
                        id="website_link"
                        label="Website Link"
                        sectionClass="field mt-2"
                        modelValue={formik.values.website_link}
                        onUpdateModelValue={(val) =>
                            formik.setFieldValue("website_link", val)
                        }
                    />

                    <div className="mb-4 mt-2">

                        <label className="mb-2">Advertisement Type</label>

                        <Select
                            value={formik.values.ad_type}
                            style={{ width: "100%", height: "42px" }}
                            onChange={(val) =>
                                formik.setFieldValue("ad_type", val)
                            }
                        >
                            <Option value="image">Banner</Option>
                            <Option value="video">Video</Option>
                        </Select>
                        {formik.touched.ad_type && typeof formik.errors.ad_type === "string" && (
                            <span id="type-help" className="p-error text-left mt-2">
                                {formik.errors.ad_type}
                            </span>
                        )}
                    </div>

                    {/* <div className="mb-4 mt-2">
            <label className="mb-2">Section</label>
            <Select
              value={formik.values.page_name}
              style={{ width: "100%", height: "42px" }}
              onChange={(val) =>
                formik.setFieldValue("page_name", val)
              }
            >
              <Option value="all">All</Option>
              <Option value="homepost">Home Post</Option>
              <Option value="homestory">Home Story</Option>
              <Option value="maintrimonial">MaitriMonial</Option>
              <Option value="users">Directory</Option>
              <Option value="business">Marketplace</Option>
              <Option value="bazarbhav">Bazar Bhav</Option>
              <Option value="job">Jobs</Option>
              <Option value="bloodgroup">Blood Group</Option>
            </Select>
          </div> */}

                    <div className="mb-4 mt-2">

                        <label className="mb-2">Section</label>

                        <MultiSelect
                            value={formik.values.page_name}
                            options={options}
                            optionLabel="label"
                            optionValue="value"
                            onChange={handleSectionChange}
                            placeholder="Select Sections"
                            display="chip"
                            maxSelectedLabels={2}
                            className="
    w-full
    min-h-[44px]
    text-sm
  "
                            panelClassName="text-sm"
                            optionDisabled={(option) =>
                                formik.values.page_name.includes("all") &&
                                option.value !== "all"
                            }
                            pt={{
                                root: {
                                    className:
                                        "w-full rounded-xl overflow-hidden"
                                },

                                labelContainer: {
                                    className:
                                        "flex flex-wrap gap-1 py-1 max-w-full overflow-hidden"
                                },

                                token: {
                                    className:
                                        "max-w-full text-xs sm:text-sm px-2 py-1 break-all whitespace-normal"
                                },

                                label: {
                                    className:
                                        "text-sm break-all whitespace-normal"
                                },

                                trigger: {
                                    className:
                                        "w-10 flex items-center justify-center flex-shrink-0"
                                },

                                panel: {
                                    className:
                                        "max-w-[95vw]"
                                },

                                item: {
                                    className:
                                        "text-sm py-2 break-all whitespace-normal"
                                }
                            }}
                        />

                    </div>


                    <div className="flex flex-col gap-2 mt-2">
                        <label>Start Date</label>
                        <Calendar
                            value={formik.values.start_date}
                            onChange={(e) =>
                                formik.setFieldValue("start_date", e.value)
                            }
                            showIcon
                            dateFormat="dd/mm/yy"
                            className="w-full"
                        />
                        {formik.touched.start_date && formik.errors.start_date && (
                            <small className="p-error">{formik.errors.start_date}</small>
                        )}
                    </div>

                    <div className="flex flex-col gap-2 mt-2">
                        <label>End Date</label>
                        <Calendar
                            value={formik.values.end_date}
                            onChange={(e) =>
                                formik.setFieldValue("end_date", e.value)
                            }
                            showIcon
                            dateFormat="dd/mm/yy"
                            className="w-full"
                        />
                        {formik.touched.end_date && formik.errors.end_date && (
                            <small className="p-error">{formik.errors.end_date}</small>
                        )}
                    </div>

                    {/* FILE */}
                    <label className="cursor-pointer block mt-4">

                        <div className="
  border-2 border-dashed border-blue-400
  rounded-xl
  p-6
  text-center
  hover:bg-blue-50
  transition
">

                            <div className="text-blue-600 font-semibold">
                                Upload Advertisement Media
                            </div>

                            <div className="text-sm text-gray-400">
                                Images (JPG, PNG) or Video (MP4) • Recommended 1080×1920 • Max 20MB
                            </div>

                        </div>

                        <input
                            type="file"
                            accept="image/*,video/*"
                            multiple
                            onChange={handleFile}
                            className="hidden"
                        />

                    </label>

                    <Button
                        label={
                            loading
                                ? advertisementId
                                    ? "Updating..."
                                    : "Submitting..."
                                : advertisementId
                                    ? "Update Advertisement"
                                    : "Submit Advertisement"
                        }
                        className="prime-button btn-login w-full mt-4"
                        type="submit"
                    />

                </form >

                {/* RIGHT PREVIEW — same card the app feed actually shows, inside a phone mockup */}
                <div className="bg-white p-6 rounded-xl shadow sticky top-4">
                    <div className="flex justify-between items-center">
                        <h2 className="text-xl font-semibold mb-4 text-gray-700">Preview</h2>
                        <span className="text-xs font-normal bg-gray-100 px-2 py-1 rounded text-gray-500 uppercase tracking-wider">9:16 Vertical</span>
                    </div>
                    <div className="flex justify-center">
                        <div className="relative w-full max-w-[320px] aspect-[9/16] bg-neutral-900 rounded-[2rem] border-[6px] border-neutral-800 shadow-2xl overflow-hidden ring-4 ring-neutral-200">
                            <div className="absolute inset-0 overflow-hidden">
                                <AdvertisementCard
                                    companyName={formik.values.company_name}
                                    media={previews.map((p) => ({ type: p.type, url: p.url }))}
                                    shortDescription={formik.values.short_description}
                                    whatsappNumber={formik.values.whatsapp_number}
                                    mobileNumber={formik.values.mobile_number}
                                    websiteLink={formik.values.website_link}
                                />
                            </div>
                        </div>
                    </div>
                </div>

            </div >

        </div >

    );

};

export default AdvertisementAdd;